<template>
    <v-row dense>
        <v-col :cols="fullWidthMobile && isMobile ? 12 : 6">
            <v-menu
                :model-value="showStartDatePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                min-width="290px"
            >
                <template v-slot:activator="{props}">
                    <v-text-field
                        :model-value="startDateInput"
                        :label="startDateLabel"
                        :clearable="clearable"
                        :single-line="singleLine"
                        variant="outlined"
                        :color="color"
                        :hide-details="hideDetails"
                        :persistent-placeholder="persistentPlaceholder"
                        :density="dense ? 'compact' : 'comfortable'"
                        bg-color="white"
                        prepend-inner-icon="mdi-calendar"
                        :type="startDateType"
                        :min="minStartDate"
                        :max="maxStartDate"
                        v-bind="props"
                        :rules="rules"
                        :class="contentClass"
                        @update:model-value="onStartDateInput"
                        @focus="onStartDateFocus(props, $event)"
                        @blur="onStartDateBlur"
                        @click:clear="onClearStartDate"
                    />
                </template>
                <v-date-picker
                    :model-value="startDate"
                    :color="color"
                    :min="minStartDate"
                    hide-header
                    :max="maxStartDate"
                    :show-current="true"
                    :title="noTitle ? '' : $vuetify.datePicker?.title"
                    @update:model-value="onChangeStartDate"
                />
            </v-menu>
        </v-col>
        <v-col :cols="fullWidthMobile && isMobile ? 12 : 6">
            <v-menu
                :model-value="showEndDatePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                min-width="290px"
            >
                <template v-slot:activator="{props}">
                    <v-text-field
                        :model-value="endDateInput"
                        :label="endDateLabel"
                        :clearable="clearable"
                        :single-line="singleLine"
                        variant="outlined"
                        :color="color"
                        :persistent-placeholder="persistentPlaceholder"
                        :hide-details="hideDetails"
                        :density="dense ? 'compact' : 'comfortable'"
                        bg-color="white"
                        prepend-inner-icon="mdi-calendar"
                        :type="endDateType"
                        :min="minEndDate"
                        :max="maxEndDate"
                        v-bind="props"
                        :rules="rules"
                        :class="contentClass"
                        @update:model-value="onEndDateInput"
                        @focus="onEndDateFocus(props, $event)"
                        @blur="onEndDateBlur"
                        @click:clear="onClearEndDate"
                    />
                </template>
                <v-date-picker
                    :model-value="endDate"
                    :color="color"
                    :min="minEndDate"
                    hide-header
                    :max="maxEndDate"
                    :show-current="true"
                    :title="noTitle ? '' : $vuetify.datePicker?.title"
                    @update:model-value="onChangeEndDate"
                />
            </v-menu>
        </v-col>
    </v-row>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue} from 'vue-facing-decorator'

    @Component
    export class Daterange extends Vue {
        @Prop({default: () => this.$t('check_in')}) startDateLabel
        @Prop({default: () => this.$t('check_out')}) endDateLabel
        @Prop({default: false}) clearable
        @Prop({default: 'primary'}) color
        @Prop({default: true}) singleLine
        // TODO: now v-date-picker don`t have prop no-title
        @Prop({default: false}) noTitle
        @Prop({default: false, type: Boolean}) persistentPlaceholder
        @Prop({default: false, type: Boolean}) dense
        @Prop({default: true}) hideDetails
        @Prop({default: () => new Date(new Date().setHours(0, 0, 0, 0))}) minStartDate
        @Prop({default: false, type: Boolean}) sameDate
        @Prop({default: () => []}) rules
        @Prop() _startDate
        @Prop() _endDate
        @Prop() contentClass
        @Prop({default: 60}) endDateDaysOffset
        @Prop({default: false, type: Boolean}) fullWidthMobile

        showStartDatePicker = false
        showEndDatePicker = false
        startDateType = 'text'
        startDateCleared = false
        startDateTmp = null
        endDateType = 'text'
        endDateCleared = false
        endDateTmp = null

        @Emit()
        changeStartDate(val) {
            return val
        }

        @Emit()
        changeEndDate(val) {
            return val
        }

        checkEndDate(newStartDate) {
            if (newStartDate >= this.endDate || this.nights(this.endDate, newStartDate) > this.endDateDaysOffset) {
                this.endDate = this.addDays(newStartDate, this.nights(this.startDate, this.endDate))
            }
        }

        onChangeStartDate(newStartDate) {
            this.startDate = newStartDate
            this.showStartDatePicker = false
            this.checkEndDate(newStartDate)
            this.showEndDatePicker = true
        }

        onChangeEndDate(newEndDate) {
            this.endDate = newEndDate
            this.showEndDatePicker = false
        }

        onStartDateInput(val) {
            if (val) this.startDateTmp = this.$dateFns.parseISO(val)
        }

        onEndDateInput(val) {
            if (val) this.endDateTmp = this.$dateFns.parseISO(val)
        }

        async onStartDateFocus(props, $event) {
            this.showStartDatePicker = true
            if (this.startDateCleared) {
                this.startDateCleared = false
                return
            }

            this.startDateTmp = this.startDate
            this.showEndDatePicker = false
            if (this.startDateType === 'text') {
                this.startDateType = 'date'
                props.onClick($event)
            }
        }

        onStartDateBlur() {
            const newStartDate = this.startDateTmp
            if (
                (!this.minStartDate || newStartDate >= this.minStartDate) &&
                (!this.maxStartDate || newStartDate <= this.maxStartDate) &&
                newStartDate !== this.startDate
            ) {
                this.checkEndDate(this.startDateTmp)
                this.startDate = this.startDateTmp
            }
            this.startDateType = 'text'
        }

        async onEndDateFocus(props, $event) {
            this.showEndDatePicker = true
            if (this.endDateCleared) {
                this.endDateCleared = false
                return
            }
            this.endDateTmp = this.endDate
            this.showStartDatePicker = false
            if (this.endDateType === 'text') {
                this.endDateType = 'date'
                props.onClick($event)
            }
        }

        onEndDateBlur() {
            const newEndDate = this.endDateTmp
            if (
                (!this.minEndDate || newEndDate >= this.minEndDate) &&
                (!this.maxEndDate || newEndDate <= this.maxEndDate) &&
                newEndDate !== this.endDate
            ) {
                this.endDate = this.endDateTmp
            }
            this.endDateType = 'text'
        }

        onClearStartDate() {
            this.startDateCleared = true
            this.startDateTmp = null
            this.startDate = null
        }

        onClearEndDate() {
            this.endDateCleared = true
            this.endDateTmp = null
            this.endDate = null
        }

        addDays(date, days) {
            if (!date || !days) return
            return this.$dateFns.addDays(date, days)
        }

        nights(startDate, endDate) {
            if (!startDate || !endDate) return
            return this.$dateFns.differenceInDays(endDate, startDate)
        }

        get firstDayOfWeekByLocale() {
            return this.$dateFns.localeOptions[this.locale].weekStartsOn
        }

        get startDateInput() {
            if (!this.startDate) return
            return this.startDateType === 'text'
                ? this.$filters.dateShortFormat(this.startDate)
                : this.$dateFns.format(this.startDateTmp)
        }

        get endDateInput() {
            if (!this.endDate) return
            return this.endDateType === 'text'
                ? this.$filters.dateShortFormat(this.endDate)
                : this.$dateFns.format(this.endDateTmp)
        }

        get locale() {
            return this.$i18n.locale
        }

        get maxStartDate() {
            return undefined
        }

        get minEndDate() {
            if (!this.startDate) return undefined
            return !this.sameDate ? this.addDays(this.startDate, 1) : this.startDate
        }

        get maxEndDate() {
            if (!this.startDate) return null

            return this.addDays(this.startDate, this.endDateDaysOffset)
        }

        get isFirefox() {
            return navigator?.userAgent.toLowerCase().indexOf('firefox') !== -1
        }

        get startDate() {
            return this._startDate
        }

        set startDate(val) {
            this.startDateTmp = val
            this.changeStartDate(val)
        }

        get endDate() {
            return this._endDate
        }

        set endDate(val) {
            this.endDateTmp = val
            this.changeEndDate(val)
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default Daterange
</script>
